// ⚠️ SHARED FOLDER — this is a NEW file. Nothing existing under
// components/_dashboard/watermarks/ was modified; this is purely additive and
// backwards-compatible (ListingWatermarkStatusChip, WatermarkRemovalDialog,
// WatermarkRemovalStudio, useListingWatermarkInfo are all untouched).
//
// WHY IT EXISTS (Haim: "I still want to be able to remove watermark on pictures from
// specific listings only ... when editing the listing in the minisite"):
// ListingWatermarkStatusChip renders `null` while the listing doc is loading AND when
// the listing has no marketplace doc / no images. On the minisite edit dialogs that made
// the per-listing entry point silently vanish — which is exactly why the capability
// looks "missing" even though it was never removed. This panel is the same feature with
// an honest, never-silent surface:
//   - loading      → a visible (disabled) button + "בודק…" so the affordance is there
//   - not tracked  → says WHY there is nothing to remove instead of disappearing
//   - partial/done → the honest per-IMAGE count, never a listing-level claim
//
// It REUSES the existing single-listing machinery and adds no removal logic of its own:
// useListingWatermarkInfo for live truth, WatermarkRemovalDialog → WatermarkRemovalStudio
// for the actual work (all-images AND image-by-image, plus before/after compare).
//
// Vocabulary is deliberately the same as the bulk flow (BulkWatermarkRemoval) and the
// studio: "X מתוך Y תמונות ללא סימני מים" / "הסרת סימני מים" / "השלמת הסרת סימני מים" /
// "צפייה בתוצאות", so the two surfaces never describe the same state differently.
import { Box, Chip, Stack, Button, Typography, CircularProgress } from "@material-ui/core";
import {
  AutoFixHigh as RemoveWatermarkIcon,
  CheckCircle as CheckIcon
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useModal } from "hooks/useModal";
import { useListingWatermarkInfo } from "./useListingWatermarkInfo";
import type { ListingWatermarkInfo } from "./useListingWatermarkInfo";
import { WatermarkRemovalDialog } from "./WatermarkRemovalDialog";
import type { ReactNode } from "react";

export type ListingWatermarkPanelProps = {
  /** Undefined when there is no listing to talk about — renders nothing. */
  listingId: string | undefined;
  /**
   * Pass an ALREADY-subscribed info when the host also reads it, so the panel does not
   * open a second snapshot listener on the same document. Omit it and the panel
   * subscribes itself.
   */
  info?: ListingWatermarkInfo;
  /** Section heading. Pass `null` when the host already renders one. */
  title?: ReactNode;
  /** Extra line rendered under the status (e.g. a switch-aware note from the host). */
  note?: ReactNode;
  /** Drop the bordered frame when the host already provides one. */
  disableFrame?: boolean;
};

/**
 * Per-listing watermark removal, as a self-contained section: live "X of Y images
 * watermark-free", an always-visible way into the removal studio (which supports
 * whole-listing AND single-image removal), and an honest explanation in every state.
 *
 * Drop it into any surface where the realtor is editing ONE listing's pictures.
 */
export function ListingWatermarkPanel({
  listingId,
  info,
  title,
  note,
  disableFrame = false
}: ListingWatermarkPanelProps): JSX.Element | null {
  const { t: translate } = useTranslation();
  const { open, openModal, closeModal } = useModal();
  // Only subscribes when the host didn't already hand us a live `info`.
  const ownInfo = useListingWatermarkInfo(info ? undefined : listingId);
  const { loading, cleanedCount, totalCount } = info ?? ownInfo;

  if (!listingId) return null;

  // `totalCount` counts the listing's images in the marketplace doc. Zero means there is
  // genuinely nothing removal could act on (webot-only listing, or no images) — the same
  // "tracked" rule the bulk status uses, so the two can never disagree.
  const tracked = totalCount > 0;
  const pendingCount = Math.max(totalCount - cleanedCount, 0);
  const allClean = tracked && pendingCount === 0;

  const statusLabel = loading
    ? translate("watermarkRemoval.statusLoading", "בודק סטטוס סימני מים…")
    : !tracked
    ? translate("watermarkRemoval.statusUntracked", "אין תמונות להסרה")
    : allClean
    ? translate("watermarkRemoval.statusAll", "כל התמונות ללא סימני מים")
    : translate(
        "watermarkRemoval.statusSome",
        "{{cleaned}} מתוך {{total}} תמונות ללא סימני מים",
        { cleaned: cleanedCount, total: totalCount }
      );

  const ctaLabel = allClean
    ? translate("watermarkRemoval.viewResults", "צפייה בתוצאות")
    : cleanedCount > 0
    ? `${translate("watermarkRemoval.finishRemoval", "השלמת הסרת סימני מים")} (${pendingCount})`
    : translate("watermarkRemoval.dialogTitle", "הסרת סימני מים");

  // HONESTY: never imply the minisite shows something it doesn't. Images without a clean
  // version always fall back to the watermarked original, and removal is a real AI job.
  const explainer = loading
    ? translate("watermarkRemoval.explainerLoading", "טוען את מצב התמונות של הנכס…")
    : !tracked
    ? translate(
        "watermarkRemoval.explainerUntracked",
        "לנכס הזה אין תמונות שאפשר להסיר מהן סימני מים."
      )
    : allClean
    ? translate(
        "watermarkRemoval.explainerAllClean",
        "לכל תמונות הנכס כבר קיימת גרסה ללא סימני מים. אפשר לפתוח כדי להשוות לפני/אחרי או להפיק מחדש תמונה בודדת."
      )
    : translate(
        "watermarkRemoval.explainerPending",
        "אפשר להסיר סימני מים מכל תמונות הנכס בבת אחת או מתמונה אחת בכל פעם. תמונות שכבר נקיות מדולגות. ההסרה מתבצעת ב-AI ולוקחת זמן — אפשר לסגור את החלון וההסרה תמשיך ברקע."
      );

  return (
    <Box
      sx={
        disableFrame
          ? undefined
          : {
              p: 1.25,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider"
            }
      }
    >
      {/* The EXISTING removal flow — studio (all images / one image) + before/after
          compare. Its writes land on the listing doc, so the live status above updates
          the moment a clean version is saved (even if a colleague generated it). */}
      <WatermarkRemovalDialog open={open} listingId={listingId} onClose={closeModal} />

      <Stack spacing={0.75}>
        {title !== null && (
          <Typography variant="subtitle2">
            {title ?? translate("watermarkRemoval.panelTitle", "סימני מים על תמונות הנכס")}
          </Typography>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Chip
            size="small"
            color={loading || !tracked ? "default" : allClean ? "success" : cleanedCount ? "info" : "warning"}
            icon={
              loading ? (
                <CircularProgress size={12} sx={{ ml: 1 }} color="inherit" />
              ) : allClean ? (
                <CheckIcon />
              ) : undefined
            }
            label={statusLabel}
          />
          {/* Rendered (disabled) while loading too — a button that silently doesn't
              exist is the whole reason this feature felt missing. */}
          {(tracked || loading) && (
            <Button
              size="small"
              variant={allClean ? "outlined" : "contained"}
              color={allClean ? "inherit" : "primary"}
              disabled={loading}
              startIcon={<RemoveWatermarkIcon />}
              onClick={(e) => {
                e.stopPropagation();
                openModal();
              }}
            >
              {ctaLabel}
            </Button>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {explainer}
        </Typography>
        {note}
      </Stack>
    </Box>
  );
}

export default ListingWatermarkPanel;
